// The lights coming on.
//
// A building's windows are painted into its base colour texture, lit or
// unlit as the photograph happened to be, and at night the whole facade
// just goes dark with everything else. This puts a mask over the glass and
// adds it back as emission, scaled by the same nightness uniform the sky
// and the moon read, so the windows come up through dusk exactly as the
// sky goes over and are gone by morning.
//
// Emissive rather than lights on purpose. A PointLight per window would be
// dozens of extra shadow-casting lights across the town for an effect that
// is entirely about the glass itself being bright. Nothing here spills onto
// the pavement, which is the honest limit of it.
//
// Adding a building:
//   1. Author a mask the same size as its base colour texture. Black
//      everywhere, white over the glass. Anything not pure black glows a
//      little in daylight too, because this is added to the lit result.
//   2. Drop it in public/textures/emissive/, named for the texture.
//   3. Add a line below, keyed by the MATERIAL name in buildings.glb.
//
// Find the material name from the texture dump: every png in the extract is
// <glb>__<image index>__<texture name>, and the material to key on is
// whichever one uses that image index. Image 25 is pinostudio, used by
// material building_02, hence the first line below.

import * as THREE from 'three/webgpu'
import { texture as tslTexture, float, vec3, color, mix } from 'three/tsl'
import { mood } from './mood'

/**
 * material name in buildings.glb -> the mask that lights it
 *
 * The key is the MATERIAL, the file is named for the TEXTURE it sits over,
 * because those are the two names that actually exist: the material is what
 * the glTF hands us at runtime, the texture name is what the extracted pngs
 * are called and therefore what a mask gets authored against. Keeping both
 * visible means the join is on the page rather than in somebody's head.
 *
 * Every mask must be the same pixel size as its base colour texture or the
 * UVs sample the wrong part of it. All thirteen below are verified matches.
 */
export const WINDOW_LIGHTS = {
  building_02: '/textures/emissive/pinostudio.ktx2',   // 512x512, the studio
  building_03: '/textures/emissive/Image_14.ktx2',     // 512x512
  'building_08.001': '/textures/emissive/Image_19.ktx2', // 1024x512
  building_08: '/textures/emissive/Image_22.ktx2',     // 512x1024
  building_09: '/textures/emissive/Image_24.ktx2',     // 512x1024
  building_11: '/textures/emissive/Image_25.ktx2',     // 512x1024
  building_12: '/textures/emissive/Image_27.ktx2',     // 512x512
  building_14: '/textures/emissive/Image_29.ktx2',     // 512x512
  building_16: '/textures/emissive/Image_31.ktx2',     // 512x1024
  building_18: '/textures/emissive/Image_34.ktx2',     // 512x512
  Shops_11: '/textures/emissive/Image_18.ktx2',        // 256x256
  Shops_14: '/textures/emissive/Image_23.ktx2',        // 256x256
  'Material.009': '/textures/emissive/Image_8.ktx2',   // 512x512
}

// Warm bulb rather than white. White reads as fluorescent and, against the
// blue cast the world takes at night, as a hole in the wall rather than a
// room with somebody in it.
export const LIGHT_COLOUR = '#ffcf8a'
// How hard they burn at full night.
//
// Higher than you would expect because the emission is multiplied by the
// building's own colour first (see below), and that texture averages well
// under half brightness, so a lot of this is just buying back what the
// multiply took.
export const LIGHT_STRENGTH = 2.4
// How much a window glows where its own texture is nearly black.
//
// At 0 the emission is purely the base colour, which is honest but loses
// the darkest blinds entirely: a black pane emits nothing and stays a hole.
// This lifts the floor so every pane reads as lit while the detail still
// does the work. Past about 0.4 it starts flattening back into the solid
// rectangle this was written to avoid.
export const LIGHT_LIFT = 0.22

// The masks are KTX2 (the .png beside each is the master; re-encode with
// scripts/ktx2-textures.mjs, sRGB, no flip: they are glTF UVs like the base
// map they sit over). Compressed textures only exist once transcoded, so
// Buildings loads all of them with the model, suspending behind the veil,
// and hands them in here; this used to start its own TextureLoader on a
// private LoadingManager mid-walk, because joining the default one late
// re-opened the veil's count.
export const WINDOW_MASKS = [...new Set(Object.values(WINDOW_LIGHTS))]

/**
 * Give a material its windows, if it has any. Safe to call on everything.
 *
 * @param {THREE.NodeMaterial} material  already converted to a node material
 * @param {string} name                  the glTF material name
 * @param {Map<string, THREE.Texture>} masks  WINDOW_MASKS, loaded, by url
 * @returns {boolean} whether it lit anything
 */
export function applyWindowLights(material, name, masks) {
  const url = WINDOW_LIGHTS[name]
  const mask = url && masks?.get(url)
  if (!mask || !material) return false

  // The node ONLY. Setting emissiveMap as well builds a second emissive node
  // from the same texture and the two stack, which reads as a blown-out
  // window rather than a lit one.
  material.emissive = new THREE.Color(0xffffff)

  // WHAT THE WINDOW EMITS IS ITS OWN CONTENT.
  //
  // The obvious version, mask × warm colour, is what the first attempt did
  // and it is wrong in a way that only shows up on screen: emission is
  // ADDED to the lit result, so a white mask paints a flat cream rectangle
  // over the glass and every blind, shelf and reflection in the photograph
  // goes with it. You get a building with five lightboxes in it.
  //
  // So the base colour goes into the emission. A dark blind emits little
  // and stays dark, bright glass emits a lot, and the pane reads as lit
  // FROM BEHIND with all of its detail intact, which is what a window with
  // the light on actually looks like from the street.
  //
  // .rgb throughout because emissive is a vec3: handing it the vec4 off a
  // texture drags alpha into the graph and the shader will not compile.
  const glass = tslTexture(mask).rgb
  const source = material.map
    // mix rather than add, so the lift cannot push a bright pane past 1 and
    // reintroduce the blowout at the top end.
    ? mix(tslTexture(material.map).rgb, vec3(1, 1, 1), float(LIGHT_LIFT))
    // No colour map is not a case that exists in buildings.glb today, but a
    // flat pane is a better failure than a black one.
    : vec3(1, 1, 1)

  // Driven as a node rather than through emissiveIntensity so it can follow
  // the shared uniform per frame without React touching the material.
  material.emissiveNode = source
    .mul(glass)
    .mul(color(LIGHT_COLOUR))
    .mul(float(LIGHT_STRENGTH))
    .mul(mood.nightness)
  material.needsUpdate = true
  return true
}

export default applyWindowLights
